import React, { useState, useLayoutEffect, useRef } from "react"
import PropTypes from "prop-types"
import styled from "styled-components"
import { Button, message, Input } from "antd"
import { UserOutlined } from "@ant-design/icons"
import TextareaAutosize from "react-textarea-autosize"

import * as api from "../../../../../api"

import { useStateValue } from "../../../../_shared/context/AppStateStore"
import useStateActions from "../../../../_shared/context/AppStateStore/actions"

import Popconfirm from "../../../../_shared/components/Popconfirm"

import ChatMessage from "./ChatMessage"

//------------------------------------------------------------------------------

function TicketDetail(props) {
  // -------------------------------------
  // Props destructuring
  // -------------------------------------

  const { ticket, onCreateMessage, onCloseTicket } = props

  // -------------------------------------
  // Hooks (e.g. useState, useMemo ...)
  // -------------------------------------

  const appState = useStateValue()
  const [{ user, token }] = appState
  const stateActions = useStateActions(appState)

  const [newMessageText, setNewMessageText] = useState("")

  const [newMessageSentBy, setNewMessageSentBy] = useState("")

  const scrollContainerDOMRef = useRef(null)
  const messagesContainerDOMRef = useRef(null)

  // -------------------------------------
  // Effects
  // -------------------------------------

  useLayoutEffect(() => {
    if (ticket) {
      stateActions.getUserUnreadedMessages()

      const messagesScrollContainerRef = messagesContainerDOMRef?.current
      messagesScrollContainerRef.scrollIntoView(false)

      // const refScrollHeight = messagesScrollContainerRef?.clientHeight

      // console.log(messagesScrollContainerRef)
      // console.log(refScrollHeight)
      // messagesScrollContainerRef.scroll(0, refScrollHeight)
    }

    //eslint-disable-next-line
  }, [ticket])

  // -------------------------------------
  // Component functions
  // -------------------------------------

  async function handleCloseTicket() {
    try {
      await api.closeTicket(ticket.id, token)

      message.success("Ticket archiviato con successo.")

      onCloseTicket && onCloseTicket()
    } catch (error) {
      message.error(
        "Non è stato possibile choudere il ticket. Riprovare più tardi."
      )
    }
  }

  async function handleCreateNewMessage() {
    if (newMessageText) {
      onCreateMessage &&
        (await onCreateMessage(ticket.id, newMessageSentBy, newMessageText))

      setNewMessageSentBy("")
      setNewMessageText("")
    } else {
        message.error("Risposta vuota")
    }
  }

  function handleNewMessageSentByValueChange(event) {
    setNewMessageSentBy(event.target.value)
  }

  function handleNewMessageValueChange(event) {
    setNewMessageText(event.target.value)
  }

  // -------------------------------------

  function renderMessages() {
    return ticket?.messages?.map((message, index) => (
      <ChatMessage
        className="chat-message"
        key={`${message.id}-${index}`}
        text={message.text}
        fromMe={message?.author?.id === user?.id}
        sentBy={message.sentBy}
      />
    ))
  }

  function renderAttachments() {
    return ticket?.attachments?.map((attachment) => {
      const attachmentType = attachment?.type

      if (attachmentType.includes("image")) {
        return (
          <a
            key={attachment?.url}
            href={attachment?.url}
            target={attachment?.url}
          >
            <img className="attachment-content" src={attachment?.url} />
          </a>
        )
      }

      if (attachmentType.includes("video")) {
        const defaultType = "video/mp4"

        return (
          <a
            key={attachment?.url}
            href={attachment?.url}
            target={attachment?.url}
          >
            <video
              key={attachment?.url}
              className="attachment-content"
              controls
            >
              <source
                src={attachment?.url}
                type={attachment?.type || defaultType}
              />
              <source src="" type='video/webm; codecs="vp8, vorbis"' />
            </video>
          </a>
        )
      }
    })
  }

  function renderCompositionBox() {
    if (ticket.status === "closed") {
      return null
    }

    return (
      <div
        className={`new-message-container${
          ticket.status === "closed" ? " disabled" : ""
        }`}
      >
        <Input
          className="new-message-input-container-sent-by"
          size="default"
          placeholder="Nome Esperto"
          prefix={<UserOutlined />}
          value={newMessageSentBy}
          onChange={handleNewMessageSentByValueChange}
          disabled={ticket.status === "closed"}
          bordered="false"
        />
        <div className="new-message-input-container">
          <TextareaAutosize
            className="new-msg-input"
            type="text"
            placeholder="Rispondi alla richiesta..."
            rows="1"
            value={newMessageText}
            onChange={handleNewMessageValueChange}
            disabled={ticket.status === "close"}
          />

          <Button
            className="send-btn"
            type="primary"
            onClick={handleCreateNewMessage}
          >
            Invia
          </Button>
        </div>
      </div>
    )
  }

  // -------------------------------------
  // Component local variables
  // -------------------------------------

  if (!ticket) {
    return null
  }

  return (
    <div className={props.className}>
      <div className="header">
        <div className="row">
          <h2>{ticket.subject}</h2>

          {ticket.status !== "closed" && (
            <Popconfirm
              title="Sei sicuro di voler chiudere il ticket?"
              placement="leftTop"
              onConfirm={handleCloseTicket}
              okText="Si"
              cancelText="No"
            >
              <Button className="close-ticket-btn" type="primary" ghost danger>
                Chiudi ticket
              </Button>
            </Popconfirm>
          )}
        </div>

        <div className="user-info">
          <span>
            {`${ticket?.user?.name} ${ticket?.user?.surname}`}
            {ticket?.user?.businessName
              ? ` - ${ticket?.user?.businessName}`
              : ""}
          </span>
          <span className="small">{ticket?.user?.email}</span>
        </div>

        <p>{ticket.description}</p>
      </div>

      <div
        className={`chat-container${
          ticket.status === "closed" ? " disabled" : ""
        }`}
      >
        <div className="scroll-container" ref={scrollContainerDOMRef}>
          {ticket?.attachments && (
            <div className="attachment-container">{renderAttachments()}</div>
          )}

          <div className="messages" ref={messagesContainerDOMRef}>
            {renderMessages()}
          </div>
        </div>

        {renderCompositionBox()}
      </div>
    </div>
  )
}

// ----------------------------------------------------------------------------
// Component PropTypes and default props
// ----------------------------------------------------------------------------

TicketDetail.propTypes = {
  className: PropTypes.string.isRequired,
  ticket: PropTypes.object,
  onCreateMessage: PropTypes.func,
  onCloseTicket: PropTypes.func,
}

TicketDetail.defaultProps = {}

// ----------------------------------------------------------------------------

const NEW_MESSAGE_INPUT_HEIGHT = 126

// ----------------------------------------------------------------------------

const StyledTicketDetail = styled(TicketDetail)`
  & {
    display: flex;
    flex-direction: column;

    height: 100%;
    max-height: 100%;

    overflow: hidden;

    > .header {
      display: flex;
      flex-direction: column;

      .user-info {
        display: flex;
        flex-direction: column;
        justify-content: center;

        height: 46px;

        span {
          color: ${(props) => props?.theme?.colors?.textSecondaryColor};
          font-weight: 600;
          line-height: 1.2;
        }

        span.small {
          font-size: 12px;
          font-weight: 500;
        }
      }

      .row {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;

        h2 {
          margin-bottom: 0;
          font-size: 32px;
          font-weight: bold;
          color: ${(props) => props?.theme?.colors?.textPrimaryColor};
        }
      }

      .close-ticket-btn {
        color: ${(props) => props?.theme?.colors?.primaryColor};
        border-color: ${(props) => props?.theme?.colors?.primaryColor};
      }

      p {
        color: ${(props) => props?.theme?.colors?.textSecondaryColor};
      }
    }

    .attachment-container {
      display: grid;
      grid-template-columns: repeat(auto-fit, 300px);
      grid-row-gap: 10px;

      grid-column-gap: 10px;

      .attachment-content {
        max-width: 100%;
        height: 200px;

        border-radius: 4px;

        outline: none;
      }
    }

    .chat-container {
      position: relative;

      flex: 1;
      display: flex;
      flex-direction: column;

      overflow: hidden;

      padding-bottom: ${NEW_MESSAGE_INPUT_HEIGHT}px;

        &.disabled {
      padding-bottom: 0;
        }

      .scroll-container {
        overflow-y: auto;
        overflow-x: hidden;

        -ms-overflow-style: none;

        ::-webkit-scrollbar {
          display: none;
        }

        .messages {
          flex: 1;

          padding: 20px 0;

          .chat-message {
            padding: 2px 10px;
          }
        }
      }

      .new-message-container {
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;

        padding: 20px;
        border: 1px solid
          ${(props) => props?.theme?.colors?.tertiaryBackgroundColor};
        border-radius: 4px;

        min-height: ${NEW_MESSAGE_INPUT_HEIGHT}px;

        background-color: ${(props) =>
          props?.theme?.colors?.secondaryBackgroundColor};

        .new-message-input-container-sent-by {
          color: ${(props) => props?.theme?.colors?.textPrimaryColor};

          border: none;
          appearance: none;
          resize: none;
          outline: none;
          margin-bottom: 20px;
          background-color: #f5f5f5;

          input {
            background-color: #f5f5f5;
          }
        }

        .new-message-input-container {
          flex: 1;

          display: flex;
          flex-direction: row;
          align-items: flex-end;

          margin-left: 11px;

          .new-msg-input {
            flex: 1;
            position: relative;

            height: 32px;

            color: ${(props) => props?.theme?.colors?.textPrimaryColor};

            border: none;
            appearance: none;
            resize: none;
            outline: none;
          }

          .send-btn {
            background-color: ${(props) => props?.theme?.colors?.primaryColor};
            border-color: ${(props) => props?.theme?.colors?.primaryColor};

            border-radius: 0 20px;
          }
        }
      }
    }
  }
`
// ----------------------------------------------------------------------------

export default StyledTicketDetail
